import { useState, useEffect, useCallback } from 'react'
import api from '../api/api'

interface LogoResponse {
  logo: string | null
}

/**
 * Hook to fetch and cache account logo
 * Returns the logo URL if available, null otherwise
 */
export const useAccountLogo = (isAuthenticated: boolean) => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchLogo = useCallback(async () => {
    if (!isAuthenticated) {
      return
    }

    // Check if logo is already cached in sessionStorage
    const cachedLogo = sessionStorage.getItem('account_logo')
    if (cachedLogo) {
      setLogoUrl(cachedLogo)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await api.get<LogoResponse>('/account/logo')
      
      if (response.data?.logo) {
        const logo = response.data.logo
        setLogoUrl(logo)
        // Cache the logo URL in sessionStorage
        sessionStorage.setItem('account_logo', logo)
      } else {
        // Handle null logo response
        setLogoUrl(null)
        // Clear cache if logo is null
        sessionStorage.removeItem('account_logo')
      }
    } catch (err) {
      console.error('Failed to fetch account logo:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch logo'))
      setLogoUrl(null)
      // Clear cache on error
      sessionStorage.removeItem('account_logo')
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    fetchLogo()
  }, [fetchLogo])

  // Listen for logo updates from other components (e.g., AccountLogoManager)
  useEffect(() => {
    const handleLogoUpdate = (event: CustomEvent) => {
      const newLogoUrl = event.detail?.logoUrl
      if (newLogoUrl) {
        setLogoUrl(newLogoUrl)
        sessionStorage.setItem('account_logo', newLogoUrl)
      } else {
        setLogoUrl(null)
        sessionStorage.removeItem('account_logo')
      }
    }

    window.addEventListener('accountLogoUpdated', handleLogoUpdate as EventListener)
    return () => {
      window.removeEventListener('accountLogoUpdated', handleLogoUpdate as EventListener)
    }
  }, [])

  // Function to clear cache (useful after upload/delete)
  const clearCache = useCallback(() => {
    sessionStorage.removeItem('account_logo')
    setLogoUrl(null)
  }, [])

  return { logoUrl, isLoading, error, refetch: fetchLogo, clearCache }
}

